/**
 * Helpers to abstract logic for code within webviews on the mobile app using enhanced web/app
 *  contract
 *  More Info: https://go.bzfd.it/enhanced-webview
 */
const isWebviewDebug = () => {
  const win = getWindow();
  const queryString = win.location.search;
  const urlParams = new URLSearchParams(queryString);
  const param = urlParams.get('wv_debug');
  return param === 'true';
};
const getWindow = () => {
  /**
   * frameElement will return value if it is from within iframe on same domain.  In that case,
   * use that as the window to synchronize values
   */
  const win = window.frameElement ? window.parent : window;
  return win;
};

/**
 * Checks whether in a webview within a BF app specifically, defaulting to using user agent
 *  but allowing for a query param indicator for debugging
 */
const isBFAppWebview = () => {
  /**
   * based on user agent, whether this is web code that is run with an app
   * regardless of integration or location (or query param assumptions)
   */
  const win = getWindow();
  const userAgent = win.navigator.userAgent.toLowerCase();
  const regex = new RegExp('buzzfeed/[0-9.]+');

  /**
   * Also look for query params - should mostly be for debugging purposes
   *  and should avoid relying on too heavily
   */
  const queryString = win.location.search;
  const urlParams = new URLSearchParams(queryString);
  const sParam = urlParams.get('s');
  const isBFAppWebview = regex.test(userAgent) || sParam === 'mobile_app';
  if (isWebviewDebug()) {
    console.log('WEBVIEW DEBUG: isBFAppWebview: ', {
      isBFAppWebview,
      userAgent,
      sParam
    });
  }
  return isBFAppWebview;
};

/**
 * Checks whether in an "enhanced" webview within a BF app specifically, defaulting to using user agent
 *  but allowing for a query param indicator for debugging
 */
const isEnhancedWebview = () => {
  const isAppWebview = isBFAppWebview();
  const win = getWindow();
  if (isAppWebview) {
    const isEnhanced = !!win?.BZFD_APP?.feature_support;
    if (isWebviewDebug()) {
      console.log('WEBVIEW DEBUG: isEnhancedWebview:', {
        isEnhancedWebview: isEnhanced,
        isBFAppWebview: isAppWebview,
        BZFD_APP: win?.BZFD_APP
      });
    }
    return isEnhanced;
  }
  if (isWebviewDebug()) {
    console.log('WEBVIEW DEBUG: isEnhancedWebview:', {
      isEnhancedWebview: false,
      isBFAppWebview: isAppWebview,
      BZFD_APP: win?.BZFD_APP
    });
  }
  return false;
};

/**
 * Checks whether a particular enhanced webview feature of particular version is supported in current page
 */
const isEnhancedWebviewFeatureSupported = _ref => {
  let {
    feature,
    version = 1
  } = _ref;
  // first check if it's in app with any enhanced webview support
  const isWebviewSupported = isEnhancedWebview();
  if (isWebviewSupported) {
    const win = getWindow();
    const features = win.BZFD_APP.feature_support || {};
    if (feature in features && features[feature].version >= version) {
      if (isWebviewDebug()) {
        console.log('WEBVIEW DEBUG: isEnhancedWebview: ', {
          isEnhancedWebviewFeatureSupported: true,
          feature,
          version,
          feature_support: win.BZFD_APP.feature_support
        });
      }
      return true;
    }
  }
  if (isWebviewDebug()) {
    console.log('WEBVIEW DEBUG: isEnhancedWebview: ', {
      isEnhancedWebviewFeatureSupported: false,
      isWebviewSupported,
      feature,
      version
    });
  }
  return false;
};

/**
 * Attempts to trigger a particular enhanced webview feature (via sending a message).
 *  Will only attempt to send if the feature is supported.  Currently only for iOS app.
 *  Example: if trying to trigger maximizing fullscreen
 *    triggerEnhancedWebviewFeature({
 *       feature: 'fullscreen', params: { direction: 'maximize' }
 *     });
 */
const triggerEnhancedWebviewFeature = _ref2 => {
  let {
    feature,
    version = 1,
    params = {}
  } = _ref2;
  const isSupported = isEnhancedWebviewFeatureSupported({
    feature,
    version,
    params
  });
  const win = getWindow();
  if (isSupported) {
    const os = win?.BZFD_APP?.app_details?.os;
    if (os && os === 'iOS' && win?.webkit?.messageHandlers?.BZFD_APP_WEBVIEW) {
      if (isWebviewDebug()) {
        console.log('WEBVIEW DEBUG: triggerEnhancedWebviewFeature: ', {
          triggeredFeature: true,
          feature,
          version,
          params,
          os: win?.BZFD_APP?.app_details?.os,
          isSupported
        });
      }
      win.webkit.messageHandlers.BZFD_APP_WEBVIEW.postMessage({
        action: feature,
        version,
        params
      });
      return true;
    }
  }
  if (isWebviewDebug()) {
    console.log('WEBVIEW DEBUG: triggerEnhancedWebviewFeature: ', {
      triggeredFeature: false,
      feature,
      version,
      params,
      os: win?.BZFD_APP?.app_details?.os,
      isSupported,
      messageHandlers: win?.webkit?.messageHandlers
    });
  }
  return false;
};
const getEnhancedTheme = () => {
  const win = getWindow();
  return win?.BZFD_APP?.app_details?.theme || 'unknown';
};
const isEnhancedDarkMode = () => {
  const win = getWindow();
  return win?.BZFD_APP?.app_details?.theme === 'dark';
};
const darkModeDefaultBackground = '#121317';

export { darkModeDefaultBackground, getEnhancedTheme, isBFAppWebview, isEnhancedDarkMode, isEnhancedWebview, isEnhancedWebviewFeatureSupported, triggerEnhancedWebviewFeature };
